import type { BreadcrumbItem } from '@powerduck/components/app/breadcrumb';

import type { AppMenuItem } from '@powerduck/components/app/menu';
import type { IAppRoute, SpaLayout } from './router-interface';
import { Component } from '@powerduck/app/vuetsx';
import { AppMenuType } from '@powerduck/common/enums/menu';
import { QueryStringUtils } from '@powerduck/common/query-string-utils';
import { isNullOrEmpty } from '@powerduck/common/utils/is-null-or-empty';
import { LanguageUtils } from '@powerduck/common/utils/language-utils';
import { PortalUtils } from '@powerduck/common/utils/utils';
import RootDynamicComponentContainer from '@powerduck/components/app/root-dynamic-component-container';
import HtmlLiteral from '@powerduck/components/html-literal/html-literal';
import { toNative, Vue } from 'vue-facing-decorator';

import { AppConfig } from '../app-config';
import { AppState } from '../app-state';
import AppRoutes from '../app-routes';
import ToolbarUserMenu from '../components/toolbar-user/toolbar-user';
import './css/common-components.css';
import './css/bootstrap-overrides.css';
import './css/app.css';
import './css/navmenu.css';
import './css/sidebar.css';
import './css/frontoffice-header.css';
import './css/shared-components.css';
import './css/mobile-system.css';
import './css/app-overrides.css';
import './classic-spinner';
import '../components/mobile-table/css/mobile-table.css';
import '../components/chip-tabs/css/chip-tabs.css';
import '../components/localized-description/css/localized-description.css';
import { Language } from '@powerduck/common/enums/language';

export interface ILazyLoadedModal {
	id: string;
	render: (h) => any;
}

interface ParsedMenuItem {
	iconPart: any;
	textPart: any;
	isSeparator: boolean;
	hasChildren: boolean;
}

@Component
class AppEntryComponent extends Vue {
	fullScreen: boolean;
	hideMenu: boolean;
	hideHeader: boolean;
	customCssClass: string;
	queryRead: boolean = undefined;
	breadcrumbItems: BreadcrumbItem[] = [];
	menuItems: AppMenuItem[] = [];
	lazyLoadedModals: ILazyLoadedModal[] = [];
	userDummy: number = 0;
	buyerMobileMenuOpen: boolean = false;
	mobileMenuOpen: boolean = false;
	mobileExpandedItems: Record<string, boolean> = {};
	mobileLangExpanded: boolean = false;
	sidebarPinned: boolean = localStorage.getItem('sidebarPinned') !== 'false';

	handleQuery() {
		if (!this.queryRead) {
			this.fullScreen = QueryStringUtils.getBool('fullscreen', false);
			if (!this.fullScreen) {
				this.fullScreen = QueryStringUtils.getBool('iframe', false);
			}

			this.hideMenu = QueryStringUtils.getBool('hidemenu', false);
			this.hideHeader = QueryStringUtils.getBool('hideheader', false);
			this.customCssClass = QueryStringUtils.getString('cssclass', null);
			this.queryRead = true;
		}

		if (AppState.currentAppRoute?.fullScreen == true) {
			this.fullScreen = true;
		}

		if (AppState.currentAppRoute?.hideMenu == true) {
			this.hideMenu = true;
		}

		this.handleDocumentTitle();
	}

	handleDocumentTitle() {
		let title = AppState.appName;
		const route = AppState.currentAppRoute;
		const routeTitle = this.getRouteTitle(route);

		if (!isNullOrEmpty(routeTitle)) {
			title = `${routeTitle} | ${title}`;
		}

		document.title = title;
	}

	getRouteTitle(route: IAppRoute): string {
		let sectionTitle: string;
		if (route != null && route.title != null) {
			try {
				sectionTitle = route.title();
			} catch (e) { }
		}

		return sectionTitle;
	}

	getRouteSubtitle(route: IAppRoute): string {
		let sectionSubtitle: string;
		if (route != null && route.subtitle != null) {
			try {
				sectionSubtitle = route.subtitle();
			} catch (e) { }
		}

		return sectionSubtitle;
	}

	getRootCssClass(): string {
		return (
			`wrapper ${this.fullScreen ? 'inv-full-screen ' : ''
			}${this.hideMenu ? 'inv-no-menu ' : ''
			}${this.hideHeader ? 'inv-no-header ' : ''
			}${!this.sidebarPinned ? 'sidebar-unpinned ' : ''
			}${this.customCssClass || ''}`
		);
	}

	handleMenuItemOnClick(menuItem: AppMenuItem) {
		PortalUtils.handleMobileMenuClick();
		menuItem.onClick();
	}

	getCurrentLayout(): SpaLayout {
		return AppState.currentAppRoute?.layout || 'admin';
	}

	render(h) {
		this.handleQuery();

		const layout = this.getCurrentLayout();
		if (layout === 'buyer' || layout === 'marketplace') {
			return this.renderFrontofficeLayout(h, layout);
		}

		return (
			<div id="app-root" class={this.getRootCssClass()}>
				{this.renderMenuComponent(h)}

				<div class="main-panel">
					{this.renderTopPanel(h, this.userDummy)}

					<div class="content">
						<div class="mobileLoader"></div>
						<router-view></router-view>
					</div>
				</div>

				<div id="bs-dtbtn-container"></div>
				<RootDynamicComponentContainer />

				{this.renderMobileMenu(h)}

				{this.lazyLoadedModals.length > 0 && this.lazyLoadedModals.map((mdl) => {
					return mdl.render(h);
				})}
			</div>
		);
	}

	renderFrontofficeLayout(h, layout: SpaLayout) {
		if (layout === 'marketplace') {
			return (
				<div id="app-root" class="marketplace-event-container container root-container inv-frontoffice-root">
					{this.renderFrontofficeHeader(h)}

					<div class="wrapper container">
						<div class="content">
							<div class="mobileLoader"></div>
							<router-view></router-view>
						</div>
					</div>

					<div id="bs-dtbtn-container"></div>
					<RootDynamicComponentContainer />

					{this.lazyLoadedModals.length > 0 && this.lazyLoadedModals.map((mdl) => {
						return mdl.render(h);
					})}
				</div>
			);
		}

		// Buyer layout: renders homepage-style nav (nav-modern) + standard Bootstrap container.
		return (
			<div id="app-root" class="inv-frontoffice-root">
				{this.renderBuyerHeader(h)}
				<div class="container">
					<div class="content">
						<div class="mobileLoader"></div>
						<router-view></router-view>
					</div>
				</div>

				<div id="bs-dtbtn-container"></div>
				<RootDynamicComponentContainer />

				{this.lazyLoadedModals.length > 0 && this.lazyLoadedModals.map((mdl) => {
					return mdl.render(h);
				})}
			</div>
		);
	}

	toggleBuyerMobileMenu() {
		this.buyerMobileMenuOpen = !this.buyerMobileMenuOpen;
	}

	renderBuyerHeader(h) {
		const user = AppState.user;
		const langList = LanguageUtils.getLanguageList();
		const currLang = langList.find(p => (p.id as string) == AppState.currentLanguageCode);
		const userClass = user ? 'logged' : 'not-logged';

		return (
			<nav class={`nav navbar fixed-top nav-modern user-${userClass}`} id="mainNavbar" style="padding: 0;">
				<div class="container-xl">
					<div class="d-flex align-items-center w-100" style="height: 5rem;">

						{/* Logo */}
						<a class="d-flex align-items-center text-decoration-none" href="/">
							<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlBlack} alt="Inviton" class="nav-logo-light nav-header-logo" />
							<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlWhite} alt="Inviton" class="nav-logo-dark nav-header-logo" />
						</a>

						{/* Desktop nav + right actions */}
						<div class="d-flex align-items-center ms-auto">
							<div class="d-none d-md-flex align-items-center">
								<a href="/features" class="nav-modern-link mx-2">Services</a>
								<a href="/events" class="nav-modern-link mx-2">
									<i class="fas fa-search me-1" style="font-size: 0.875rem;"></i>
									Events
								</a>
								<a href="javascript:void(0)" class="nav-modern-link mx-2" data-bs-toggle="modal" data-bs-target="#sendFeedbackModal">Contact</a>
							</div>

							{/* Theme toggle */}
							<button class="btn p-2 text-gray-400 me-2" type="button" onClick={() => this.toggleTheme()} aria-label="Toggle theme">
								<i class="fas fa-sun nav-theme-light" style="font-size: 1.125rem;"></i>
								<i class="far fa-moon nav-theme-dark" style="font-size: 1.125rem;"></i>
							</button>

							{/* Language dropdown */}
							<div class="dropdown me-3">
								<a class="d-flex align-items-center text-decoration-none" href="#" id="navbarDropdownBuyer" role="button" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
									<img src={LanguageUtils.getLanguageFlagUrl(currLang?.flag, true)} class="img-fluid rounded" width="28" alt={AppState.currentLanguageCode} />
								</a>
								<div class="dropdown-menu dropdown-menu-right glass-panel-lg" style="min-width: 140px;" aria-labelledby="navbarDropdownBuyer">
									{langList.map(lang => (
										<a class="dropdown-item d-flex align-items-center px-3 py-2 text-gray-300" href="javascript:" onClick={() => AppState.setLanguage(lang.id as string, true)}>
											<img src={LanguageUtils.getLanguageFlagUrl(lang.flag, false)} class="img-fluid rounded me-2" width="24" alt={lang.id as string} />
											<span>{lang.text}</span>
										</a>
									))}
								</div>
							</div>

							{/* Divider */}
							<div class="d-none d-md-block bg-white-10 mx-2" style="width: 1px; height: 1.5rem;"></div>

							{/* User menu or login */}
							{!user ? (
								<a href="/login" class="nav-modern-link d-none d-md-inline mx-2">Login</a>
							) : (
								<div class="dropdown menu-user d-none d-md-block">
									<a href="javascript:" class="d-flex align-items-center justify-content-center bg-white-10 rounded-full text-white dropdown-toggle" id="buyerUserMenuBtn" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false" style="width: 2.5rem; height: 2.5rem;">
										<i class="fas fa-user"></i>
									</a>
									<div class="dropdown-menu dropdown-menu-right glass-panel-lg" style="min-width: 240px;">
										<div class="user-menu">
											<div class="text-center p-3 border-bottom-separator">
												<div class="text-white font-weight-medium">{user.DisplayName || user.Email}</div>
												{user.DisplayName && user.DisplayName !== user.Email && <small class="text-gray-400">{user.Email}</small>}
											</div>
											<div class="py-2">
												<router-link class="dropdown-item d-flex align-items-center px-3 py-2 text-gray-300" to={AppRoutes.myTickets.path}>
													<i class="fa fa-tags me-3 text-gray-500" style="width: 1rem;"></i>
													My tickets
												</router-link>
												<router-link class="dropdown-item d-flex align-items-center px-3 py-2 text-gray-300" to={AppRoutes.editProfile.path}>
													<i class="fa fa-user me-3 text-gray-500" style="width: 1rem;"></i>
													Edit profile
												</router-link>
											</div>
											<div class="border-top-separator py-2">
												<router-link class="dropdown-item d-flex align-items-center px-3 py-2 text-gray-300" to={AppRoutes.eventManagementSelection.path}>
													<i class="fa fa-calendar me-3 text-gray-500" style="width: 1rem;"></i>
													My events
												</router-link>
											</div>
											<div class="border-top-separator py-2">
												<a class="dropdown-item d-flex align-items-center px-3 py-2 text-gray-300" href="/logout">
													<i class="fa fa-power-off me-3 text-gray-500" style="width: 1rem;"></i>
													Logout
												</a>
											</div>
										</div>
									</div>
								</div>
							)}

							{/* Mobile menu toggle */}
							<button class="d-md-none mobile-menu-btn" type="button" aria-label="Toggle navigation" onClick={() => this.toggleBuyerMobileMenu()}>
								<i class={this.buyerMobileMenuOpen ? 'icon-close' : 'icon-menu'}></i>
							</button>
						</div>
					</div>
				</div>

				{/* Mobile menu */}
				<div class={`bg-dark-900 border-top-separator${this.buyerMobileMenuOpen ? ' mobile-menu-open' : ''}`} id="mobileMenu">
					<div class="container-xl py-3">
						<div class="py-2">
							<a href="/features" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">Services</a>
							<a href="/events" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">Events</a>
							<a href="javascript:void(0)" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded" data-bs-toggle="modal" data-bs-target="#sendFeedbackModal" onClick={() => this.toggleBuyerMobileMenu()}>Contact</a>
						</div>
						<div class="border-top-separator pt-3 mt-2">
							<button class="d-flex align-items-center w-100 py-2 px-3 text-gray-300 rounded border-0" style="background: transparent;" type="button" onClick={() => { this.toggleTheme(); this.toggleBuyerMobileMenu(); }}>
								<i class="fas fa-sun nav-theme-light me-3" style="width: 1rem;"></i>
								<i class="far fa-moon nav-theme-dark me-3" style="width: 1rem;"></i>
							</button>
						</div>
						<div class="border-top-separator pt-3 mt-2">
							{!user ? (
								<a href="/login" class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">
									<i class="fas fa-sign-in-alt me-2"></i> Login
								</a>
							) : (
								<div>
									<router-link to={AppRoutes.myTickets.path} class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">
										<i class="fa fa-tags me-2"></i> My tickets
									</router-link>
									<router-link to={AppRoutes.editProfile.path} class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">
										<i class="fa fa-user me-2"></i> Edit profile
									</router-link>
									<router-link to={AppRoutes.eventManagementSelection.path} class="d-block py-2 px-3 text-gray-300 text-decoration-none rounded">
										<i class="fa fa-calendar me-2"></i> My events
									</router-link>
									<div class="border-top-separator mt-2 pt-2">
										<a href="/logout" class="d-block py-2 px-3 text-gray-400 text-decoration-none rounded">
											<i class="fa fa-power-off me-2"></i> Logout
										</a>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</nav>
		);
	}

	renderFrontofficeHeader(h) {
		const user = AppState.user;
		const langList = LanguageUtils.getLanguageList();
		const currLang = langList.find(p => (p.id as string) == AppState.currentLanguageCode);

		return (
			<nav class="frontoffice-header">
				<div class="header-inner">
					<a class="d-flex align-items-center text-decoration-none" href="/">
						<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlBlack} alt="Inviton" class="header-logo logo-light" />
						<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlWhite} alt="Inviton" class="header-logo logo-dark" />
					</a>

					<div class="header-nav">
						<div class="d-none d-md-flex align-items-center">
							<a href="/" class="header-nav-link">Home</a>
						</div>

						{/* Theme toggle */}
						<button class="theme-toggle" type="button" onClick={() => this.toggleTheme()} aria-label="Toggle theme">
							<i class="fas fa-sun icon-theme-light"></i>
							<i class="far fa-moon icon-theme-dark"></i>
						</button>

						{/* Language dropdown */}
						<div class="dropdown ms-2">
							<a class="d-flex align-items-center text-decoration-none" href="#" data-bs-toggle="dropdown" aria-expanded="false">
								<img src={LanguageUtils.getLanguageFlagUrl(currLang?.flag, true)} class="lang-flag" alt={AppState.currentLanguageCode} />
							</a>
							<div class="dropdown-menu dropdown-menu-end">
								{langList.map(lang => (
									<a class="dropdown-item d-flex align-items-center px-3 py-2" href="javascript:" onClick={() => AppState.setLanguage(lang.id as string, true)}>
										<img src={LanguageUtils.getLanguageFlagUrl(lang.flag, false)} class="img-fluid rounded me-2" width="24" alt={lang.id as string} />
										<span>{lang.text}</span>
									</a>
								))}
							</div>
						</div>

						{/* Divider */}
						<div class="header-divider d-none d-md-block"></div>

						{!user ? (
							<a href="/login" class="header-nav-link d-none d-md-inline">Login</a>
						) : (
							<div class="dropdown d-none d-md-block">
								<a href="javascript:" class="user-avatar dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
									<i class="fas fa-user"></i>
								</a>
								<div class="dropdown-menu dropdown-menu-end user-menu" style="min-width: 240px;">
									<div class="text-center p-3 border-sep">
										<div class="fw-medium">{user.DisplayName || user.Email}</div>
										{user.DisplayName && user.DisplayName !== user.Email && <small class="text-muted">{user.Email}</small>}
									</div>
									<div class="py-1">
										<router-link class="dropdown-item px-3 py-2" to={AppRoutes.myTickets.path}><i class="fa fa-tags me-2"></i>My tickets</router-link>
										<router-link class="dropdown-item px-3 py-2" to={AppRoutes.editProfile.path}><i class="fa fa-user me-2"></i>Edit profile</router-link>
									</div>
									<div class="py-1 border-sep">
										<router-link class="dropdown-item px-3 py-2" to={AppRoutes.eventManagementSelection.path}><i class="fa fa-calendar me-2"></i>My events</router-link>
									</div>
									<div class="py-1 border-sep">
										<a class="dropdown-item px-3 py-2" href="/logout"><i class="fa fa-power-off me-2"></i>Logout</a>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</nav>
		);
	}

	toggleTheme() {
		const html = document.documentElement;
		if (html.classList.contains('dark')) {
			html.classList.remove('dark');
			localStorage.setItem('theme', 'light');
		} else {
			html.classList.add('dark');
			localStorage.setItem('theme', 'dark');
		}
	}

	renderMenuComponent(h) {
		if (this.hideMenu || this.fullScreen) {
			return <div></div>;
		} else {
			const langList = LanguageUtils.getLanguageList();
			const currLang = langList.find(p => (p.id as string) == AppState.currentLanguageCode);
			const menuIndexHolder: AppMenuIndexHolder = { index: 1 };

			return (
				<div class="sidebar" data-color="black">
					<div class="logo">
						<a href="/" class="simple-text logo-mini">
							<img src={AppConfig.logoPathSquare} />
						</a>
						<a href="/" class="simple-text logo-normal">
							<img style="height: 30px;" src={AppConfig.logoPathRectangle} />
						</a>
						<div class="navbar-minimize">
							<button id="minimizeSidebar" class={`sidebar-pin-btn${this.sidebarPinned ? ' pinned' : ''}`} onClick={() => this.toggleSidebarPin()} title={this.sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar'}>
								<i class="fas fa-bars"></i>
							</button>
						</div>
					</div>
					<div class="sidebar-wrapper ps-container">
						<div class="user">
							<div class="photo" style={`background-image:url('${LanguageUtils.getLanguageFlagUrl(currLang?.flag, true)}');`}></div>
							<div class="info">
								<a data-bs-toggle="collapse" href="#menuCollapseLanguage" class="collapsed">
									<span>
										{(currLang?.id as string)?.toUpperCase()}
										<b class="caret"></b>
									</span>
								</a>
								<div class="clearfix"></div>
								<div class="collapse" id="menuCollapseLanguage">
									<ul class="nav">
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.cs), 'Česky')}
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.de), 'Deutsch')}
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.en), 'English')}
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.hu), 'Magyar')}
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.pl), 'Polski')}
										{this.renderMenuLanguageItem(h, langList.find(p => (p.id as string) == Language.sk), 'Slovensky')}
									</ul>
								</div>
							</div>
						</div>

						{this.renderMenuItemSet(h, menuIndexHolder, (this.menuItems || []), 0)}
					</div>

					{/* User Profile — bottom of sidebar */}
					{AppState.user != null &&
						<div class="sidebar-user-profile">
							<div class="sidebar-user-separator"></div>
							<a href="javascript:" class="sidebar-theme-row" onClick={() => this.toggleTheme()}>
								<i class="fas fa-moon sidebar-theme-icon-dark"></i>
								<i class="fas fa-sun sidebar-theme-icon-light"></i>
								<span class="sidebar-theme-label sidebar-theme-label-dark">Dark mode</span>
								<span class="sidebar-theme-label sidebar-theme-label-light">Light mode</span>
								<span class="sidebar-theme-toggle">
									<span class="sidebar-theme-toggle-knob"></span>
								</span>
							</a>
							<a href="javascript:" class="sidebar-user-row" data-bs-toggle="dropdown" data-bs-display="static">
								<div class="sidebar-user-avatar">
									<i class="fas fa-user"></i>
								</div>
								<div class="sidebar-user-info">
									<span class="sidebar-user-name">{AppState.user.DisplayName}</span>
									<span class="sidebar-user-email">{AppState.user.Email}</span>
								</div>
								<b class="caret sidebar-user-caret"></b>
							</a>
							<div class="dropdown-menu sidebar-user-dropdown">
								<div class="sud-header">
									<div class="sud-header-avatar">
										<i class="fas fa-user"></i>
									</div>
									<span class="sud-header-email">{AppState.user.Email}</span>
								</div>
								<div class="dropdown-divider"></div>
								<h6 class="sud-section-label">{AppState.resources.menuVisitor}</h6>
								<a class="dropdown-item" href="/home">
									<i class="fa fa-calendar"></i>
									{AppState.resources.menuEvents}
								</a>
								<router-link class="dropdown-item" to={AppRoutes.myTickets.path}>
									<i class="fa fa-tags"></i>
									{AppState.resources.menuMyTickets}
								</router-link>
								<router-link class="dropdown-item" to={AppRoutes.editProfileNew.path}>
									<i class="fa fa-user"></i>
									{AppState.resources.editProfileBreadcrumbTitle}
								</router-link>
								<div class="dropdown-divider"></div>
								<h6 class="sud-section-label">{AppState.resources.promoterInfoShort}</h6>
								<router-link class="dropdown-item" to={AppRoutes.createEvent.path}>
									<i class="fas fa-plus-circle"></i>
									{AppState.resources.createNewEvent}
								</router-link>
								<router-link class="dropdown-item" to={AppRoutes.eventManagementSelection.path}>
									<i class="fa fa-calendar"></i>
									{AppState.resources.menuMyEvents}
								</router-link>
								<router-link class="dropdown-item" to={AppRoutes.accessControlCheckingSelection.path}>
									<i class="far fa-check-square"></i>
									{AppState.resources.menuAccessControl}
								</router-link>
								<div class="dropdown-divider"></div>
								<a class="dropdown-item sud-logout" href="/logout">
									<i class="fas fa-power-off"></i>
									{AppState.resources.menuLogout}
								</a>
							</div>
						</div>
					}
				</div>
			);
		}
	}

	renderMenuLanguageItem(
		h,
		lang: LanguageUtils.LanguageListItem,
		langText: string,
	) {
		return (
			<li>
				<a onClick={() => AppState.setLanguage(lang.id as string, true)} href="javascript:">
					<span class="sidebar-mini-icon">
						<span class="photo" style={`background-image:url('${LanguageUtils.getLanguageFlagUrl(lang.flag, true)}');`}></span>
					</span>
					<span class="sidebar-normal">{langText}</span>
				</a>
			</li>
		);
	}

	renderMenuItemSet(h, indexHolder: AppMenuIndexHolder, arr: AppMenuItem[], level: number) {
		return (
			<ul class={`nav nav-level-${level}`}>
				{arr.map((menuItem) => {
					const hasChildren = !isNullOrEmpty(menuItem.children);
					const isSeparator = !hasChildren && menuItem.type == AppMenuType.Separator;
					let iconIsText = false;
					let iconPart, textPart;

					if (!isSeparator) {
						let iconValue = menuItem.icon;
						if (isNullOrEmpty(iconValue) && isNullOrEmpty(menuItem.iconHtml)) {
							const splitArr = (menuItem.text || '').split(' ');
							iconValue = (splitArr[0] || ' ').substring(0, 1) + (splitArr[1] || ' ').substring(0, 1);
						}

						if (!isNullOrEmpty(menuItem.iconHtml)) {
							iconPart = (
								<span class="sidebar-mini-icon sidebar-mini-icon-html">
									<HtmlLiteral innerHTML={menuItem.iconHtml} />
								</span>
							);
						} else if (iconValue && iconValue.length > 3) {
							iconPart = (<i class={iconValue}></i>);
						} else {
							iconIsText = true;
							iconPart = (<span class="sidebar-mini-icon">{iconValue}</span>);
						}
					}

					if (!iconIsText) {
						textPart = (<p> {menuItem.text} {hasChildren && <b class="caret"></b>} </p>);
					} else {
						textPart = (<span class="sidebar-normal"> {menuItem.text} {hasChildren && <b class="caret"></b>} </span>);
					}

					if (isSeparator) {
						return (<li class="sidebar-nav-separator"></li>);
					} else if (!hasChildren) {
						return (
							<li class={menuItem.active ? 'active' : ''}>
								{this.renderMenuItemAnchor(h, menuItem, iconPart, textPart)}
							</li>
						);
					} else {
						const menuItemId = `menuCollapseItem${indexHolder.index}`;
						indexHolder.index += 1;

						return (
							<li class={menuItem.active ? 'active' : ''}>
								<a data-bs-toggle="collapse" href={`#${menuItemId}`} class={menuItem.active ? '' : 'collapsed'} aria-expanded={menuItem.active}>
									{iconPart}
									{textPart}
								</a>
								<div class="clearfix"></div>
								<div class={menuItem.active ? 'collapse show' : 'collapse'} id={menuItemId}>
									{this.renderMenuItemSet(h, indexHolder, menuItem.children, level + 1)}
								</div>
							</li>
						);
					}
				})}
			</ul>
		);
	}

	renderMenuItemAnchor(
		h,
		menuItem: AppMenuItem,
		iconPart,
		textPart,
	) {
		if (menuItem.routerPath) {
			return (
				<router-link to={menuItem.url}>
					{iconPart}
					{textPart}
				</router-link>
			);
		} else if (menuItem.url) {
			return (
				<a href={menuItem.url}>
					{iconPart}
					{textPart}
				</a>
			);
		} else if (menuItem.onClick) {
			return (
				<a href="javascript:" onClick={() => { this.handleMenuItemOnClick(menuItem); }}>
					{iconPart}
					{textPart}
				</a>
			);
		}
	}

	onMobileHamburgerClick() {
		this.mobileMenuOpen = true;
		document.body.style.overflow = 'hidden';
	}

	mobileMenuClosing: boolean = false;

	closeMobileMenu() {
		this.mobileMenuClosing = true;
		setTimeout(() => {
			this.mobileMenuOpen = false;
			this.mobileMenuClosing = false;
			this.mobileLangExpanded = false;
			this.mobileExpandedItems = {};
			document.body.style.overflow = '';
		}, 250);
	}

	toggleMobileExpand(key: string) {
		this.mobileExpandedItems = {
			...this.mobileExpandedItems,
			[key]: !this.mobileExpandedItems[key]
		};
	}

	toggleSidebarPin() {
		this.sidebarPinned = !this.sidebarPinned;
		localStorage.setItem('sidebarPinned', String(this.sidebarPinned));
	}

	renderTopPanel(h, dummyUser) {
		if (this.hideMenu || this.fullScreen) {
			return <div></div>;
		} else {
			const route = AppState.currentAppRoute;
			const routerTitle = this.getRouteTitle(route);
			const breadcrumbList = (this.breadcrumbItems || []) as BreadcrumbItem[];
			const breadcrumbLastIndex = breadcrumbList.length - 1;
			const hasBreadcrumb = breadcrumbList.length > 0;
			const langList = LanguageUtils.getLanguageList();
			const currLang = langList.find(p => (p.id as string) == AppState.currentLanguageCode);

			return (
				<div class="topnavbar-wrap">
					{/* Mobile header — visible only on ≤576px */}
					<div class="mobile-topbar">
						<a href="/" class="mobile-brand">
							<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlBlack} alt="Inviton" class="mobile-brand-logo mobile-brand-logo--light" />
							<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlWhite} alt="Inviton" class="mobile-brand-logo mobile-brand-logo--dark" />
						</a>
						<div class="mobile-nav-right">
							<button class="mobile-nav-btn" type="button" onClick={() => this.toggleTheme()} aria-label="Toggle theme">
								<i class="far fa-moon"></i>
							</button>
							<div class="dropdown mobile-lang-dropdown">
								<a href="#" class="mobile-lang-trigger" data-bs-toggle="dropdown" aria-expanded="false">
									<img src={LanguageUtils.getLanguageFlagUrl(currLang?.flag, true)} class="mobile-lang-flag" alt={AppState.currentLanguageCode} />
								</a>
								<div class="dropdown-menu dropdown-menu-end mobile-lang-menu">
									{langList.map(lang => (
										<a class="dropdown-item mobile-lang-item" href="javascript:" onClick={() => AppState.setLanguage(lang.id as string, true)}>
											<img src={LanguageUtils.getLanguageFlagUrl(lang.flag, true)} class="mobile-lang-item-flag" alt={lang.id as string} />
											<span>{lang.text}</span>
										</a>
									))}
								</div>
							</div>
							<div class="dropdown mobile-user-dropdown">
								<a href="#" class="mobile-avatar" data-bs-toggle="dropdown" aria-expanded="false">
									<i class="fas fa-user"></i>
								</a>
								{AppState.user != null &&
									<div class="dropdown-menu dropdown-menu-end mobile-user-menu">
										<div class="mobile-user-header">
											<div class="mobile-user-header-avatar">
												<i class="fas fa-user"></i>
											</div>
											<div class="mobile-user-header-info">
												<span class="mobile-user-name">{AppState.user.DisplayName}</span>
												{AppState.user.DisplayName !== AppState.user.Email &&
													<span class="mobile-user-email">{AppState.user.Email}</span>
												}
											</div>
										</div>
										<div class="mobile-user-divider"></div>
										<h6 class="mobile-user-section">{AppState.resources.menuVisitor}</h6>
										<a class="dropdown-item mobile-user-item" href="/home">
											<i class="fa fa-calendar"></i>
											<span>{AppState.resources.menuEvents}</span>
										</a>
										<router-link class="dropdown-item mobile-user-item" to={AppRoutes.myTickets.path}>
											<i class="fa fa-tags"></i>
											<span>{AppState.resources.menuMyTickets}</span>
										</router-link>
										<router-link class="dropdown-item mobile-user-item" to={AppRoutes.editProfileNew.path}>
											<i class="fa fa-user"></i>
											<span>{AppState.resources.editProfileBreadcrumbTitle}</span>
										</router-link>
										<div class="mobile-user-divider"></div>
										<h6 class="mobile-user-section">{AppState.resources.promoterInfoShort}</h6>
										<router-link class="dropdown-item mobile-user-item" to={AppRoutes.createEvent.path}>
											<i class="fas fa-plus-circle"></i>
											<span>{AppState.resources.createNewEvent}</span>
										</router-link>
										<router-link class="dropdown-item mobile-user-item" to={AppRoutes.eventManagementSelection.path}>
											<i class="fa fa-calendar"></i>
											<span>{AppState.resources.menuMyEvents}</span>
										</router-link>
										<router-link class="dropdown-item mobile-user-item" to={AppRoutes.accessControlCheckingSelection.path}>
											<i class="far fa-check-square"></i>
											<span>{AppState.resources.menuAccessControl}</span>
										</router-link>
										<div class="mobile-user-divider"></div>
										<a class="dropdown-item mobile-user-item mobile-user-logout" href="/logout">
											<i class="fas fa-power-off"></i>
											<span>{AppState.resources.menuLogout}</span>
										</a>
									</div>
								}
							</div>
							<button class="mobile-nav-btn mobile-hamburger" type="button" onClick={() => this.onMobileHamburgerClick()} aria-label="Menu">
								<i class="fas fa-bars"></i>
							</button>
						</div>
					</div>

					{/* Desktop navbar — hidden on ≤576px */}
					<nav class={`navbar navbar-expand-lg navbar-transparent navbar-absolute bg-primary ${hasBreadcrumb ? 'has-breadcrumb ' : ''}`}>
						<div class="container-fluid">
							<div class="navbar-wrapper">
								<div class="navbar-toggle">
									<button type="button" class="navbar-toggler">
										<span class="navbar-toggler-bar bar1"></span>
										<span class="navbar-toggler-bar bar2"></span>
										<span class="navbar-toggler-bar bar3"></span>
									</button>
								</div>

								<span class="navbar-title-bc">
									<span class="navbar-brand pg-title">{!isNullOrEmpty(routerTitle) ? routerTitle : ''}</span>

									{hasBreadcrumb &&
										<nav class="breadcrumb-wrap" aria-label="breadcrumb">
											<ol class="breadcrumb">
												{breadcrumbList.map((item, i) => {
													let breadcrumbItem;
													const lastItem = (i == breadcrumbLastIndex);

													if (isNullOrEmpty((item as any).icon)) {
														breadcrumbItem = ((item as any).text);
													} else {
														breadcrumbItem = (<span class="breadcrumbitem-wi"><i class={(item as any).icon}></i>{(item as any).text}</span>);
													}

													if (!lastItem) {
														return (<li class="breadcrumb-item"><a href={!isNullOrEmpty((item as any).url) ? (item as any).url : 'javascript:'}>{breadcrumbItem}</a></li>);
													} else {
														return (<li class="breadcrumb-item active" aria-current="page">{breadcrumbItem}</li>);
													}
												})}
											</ol>
										</nav>
									}
								</span>
							</div>

							{ToolbarUserMenu.renderMenu(this.userDummy)}
						</div>
					</nav>

					<div class="panel-header panel-header-sm"></div>
				</div>
			);
		}
	}

	renderMobileMenu(h) {
		if (!this.mobileMenuOpen) return null;

		const langList = LanguageUtils.getLanguageList();
		const currLang = langList.find(p => (p.id as string) == AppState.currentLanguageCode);
		const menuItems = this.menuItems || [];

		// Split menu items into groups by separators
		const groups: AppMenuItem[][] = [];
		let current: AppMenuItem[] = [];
		for (const item of menuItems) {
			if (item.type == AppMenuType.Separator) {
				if (current.length > 0) groups.push(current);
				current = [];
			} else {
				current.push(item);
			}
		}
		if (current.length > 0) groups.push(current);

		// First group: navigation (first 2 items) + event management (rest)
		const navItems = groups.length > 0 ? groups[0].slice(0, 2) : [];
		const mgmtItems = groups.length > 0 && groups[0].length > 2 ? groups[0].slice(2) : [];
		const extraGroups = groups.slice(1);

		return (
			<div class={`mmenu-overlay${this.mobileMenuClosing ? ' mmenu-closing' : ''}`}>
				<div class="mmenu-header">
					<a href="/" class="mmenu-brand" onClick={() => this.closeMobileMenu()}>
						<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlBlack} alt="Inviton" class="mmenu-brand-logo mmenu-brand-logo--light" />
						<img src={AppState.instanceConfig?.PortalSettings?.LogoUrlWhite} alt="Inviton" class="mmenu-brand-logo mmenu-brand-logo--dark" />
					</a>
					<button class="mmenu-close" type="button" onClick={() => this.closeMobileMenu()}>
						<i class="fas fa-times"></i>
					</button>
				</div>

				<div class="mmenu-body">
					{AppState.user != null &&
						<router-link to={AppRoutes.editProfileNew.path} class="mmenu-user-card" onClick={() => this.closeMobileMenu()}>
							<div class="mmenu-user-avatar">
								<i class="fas fa-user"></i>
							</div>
							<div class="mmenu-user-info">
								<span class="mmenu-user-name">{AppState.user.DisplayName}</span>
								<span class="mmenu-user-email">{AppState.user.Email}</span>
							</div>
							<i class="fas fa-chevron-right mmenu-user-chevron"></i>
						</router-link>
					}

					{navItems.length > 0 && <h6 class="mmenu-section">Navigácia</h6>}
					{navItems.map((item, i) => this.renderMobileMenuItem(h, item, `nav-${i}`))}

					{mgmtItems.length > 0 && <h6 class="mmenu-section">Správa eventu</h6>}
					{mgmtItems.map((item, i) => this.renderMobileMenuItem(h, item, `mgmt-${i}`))}

					{extraGroups.length > 0 && <div class="mmenu-separator"></div>}
					{extraGroups.map((group, gi) =>
						group.map((item, i) => this.renderMobileMenuItem(h, item, `extra-${gi}-${i}`))
					)}
				</div>

				<div class="mmenu-footer">
					<div class="mmenu-footer-top">
						<a href="javascript:" class="mmenu-lang-trigger" onClick={() => { this.mobileLangExpanded = !this.mobileLangExpanded; }}>
							<img src={LanguageUtils.getLanguageFlagUrl(currLang?.flag, true)} class="mmenu-lang-flag" alt={AppState.currentLanguageCode} />
							<span class="mmenu-lang-text">{currLang?.text || 'Language'}</span>
							<i class={`fas fa-chevron-${this.mobileLangExpanded ? 'down' : 'right'} mmenu-lang-chevron`}></i>
						</a>
						<button class="mmenu-theme-btn" type="button" onClick={() => this.toggleTheme()}>
							<i class="far fa-moon"></i>
						</button>
					</div>

					{this.mobileLangExpanded &&
						<div class="mmenu-lang-list">
							{langList.map(lang => (
								<a class="mmenu-lang-item" href="javascript:" onClick={() => { AppState.setLanguage(lang.id as string, true); this.closeMobileMenu(); }}>
									<img src={LanguageUtils.getLanguageFlagUrl(lang.flag, true)} class="mmenu-lang-item-flag" alt={lang.id as string} />
									<span>{lang.text}</span>
								</a>
							))}
						</div>
					}

					<a class="mmenu-logout" href="/logout">
						<i class="fas fa-sign-out-alt"></i>
						<span>{AppState.resources.menuLogout}</span>
					</a>
				</div>
			</div>
		);
	}

	renderMobileMenuItem(h, item: AppMenuItem, key: string) {
		const hasChildren = !isNullOrEmpty(item.children);
		const isExpanded = this.mobileExpandedItems[key];

		let iconEl;
		if (!isNullOrEmpty(item.iconHtml)) {
			iconEl = <span class="mmenu-item-icon"><HtmlLiteral innerHTML={item.iconHtml} /></span>;
		} else if (item.icon && item.icon.length > 3) {
			iconEl = <span class="mmenu-item-icon"><i class={item.icon}></i></span>;
		} else {
			const words = (item.text || '').split(' ');
			const initials = (words[0] || ' ').substring(0, 1) + (words[1] || ' ').substring(0, 1);
			iconEl = <span class="mmenu-item-icon mmenu-item-icon-text">{initials}</span>;
		}

		const activeClass = item.active ? ' mmenu-item--active' : '';

		if (hasChildren) {
			return (
				<div>
					<a href="javascript:" class={`mmenu-item${activeClass}`} onClick={() => this.toggleMobileExpand(key)}>
						{iconEl}
						<span class="mmenu-item-text">{item.text}</span>
						<i class={`fas fa-chevron-${isExpanded ? 'down' : 'right'} mmenu-item-chevron`}></i>
					</a>
					{isExpanded &&
						<div class="mmenu-subitems">
							{item.children.map((child, ci) => this.renderMobileMenuItem(h, child, `${key}-c${ci}`))}
						</div>
					}
				</div>
			);
		}

		if (item.onClick) {
			return (
				<a href="javascript:" class={`mmenu-item${activeClass}`} onClick={() => { item.onClick(); this.closeMobileMenu(); }}>
					{iconEl}
					<span class="mmenu-item-text">{item.text}</span>
				</a>
			);
		} else if (item.routerPath) {
			return (
				<router-link to={item.url} class={`mmenu-item${activeClass}`} onClick={() => this.closeMobileMenu()}>
					{iconEl}
					<span class="mmenu-item-text">{item.text}</span>
				</router-link>
			);
		} else {
			return (
				<a href={item.url || '#'} class={`mmenu-item${activeClass}`} onClick={() => this.closeMobileMenu()}>
					{iconEl}
					<span class="mmenu-item-text">{item.text}</span>
				</a>
			);
		}
	}
}

interface AppMenuIndexHolder {
	index: number;
}

export default toNative(AppEntryComponent);
